import type { Sex } from "@/components/company-census/types";
import { loadCompanyCensusEnv } from "@/env";
import { createApiClient } from "@cakewalk/frontend-api";

/**
 * Authenticated client for the company-census "Connect BambooHR" step
 * (platform-API routes at `/api/quote-census/bamboohr`).
 *
 *   authorize → mints the BambooHR OAuth URL for the caller's draft
 *   result    → the persisted pull, mapped to census-facing shapes
 *
 * The OAuth callback itself is a full-page redirect handled server-side + the
 * `/oauth/bamboohr/callback` popup page — not called from here.
 *
 * Unlike the QuickBooks client, `authorize` takes the employer's BambooHR
 * subdomain: BambooHR's authorize and token URLs are both per-tenant, so the
 * server cannot mint the URL without it.
 */

const BAMBOO_PATH = "/api/quote-census/bamboohr";

function baseUrl(): string {
  return loadCompanyCensusEnv().VITE_API_BASE_URL.replace(/\/$/, "");
}

function client() {
  return createApiClient({ baseUrl: baseUrl(), requireAuth: true });
}

export interface CensusBambooHrAuthorizeResponse {
  authorizeUrl: string;
}

/** A census member as returned by the BambooHR pull (names may be blank). */
export interface CensusBambooHrMemberWire {
  /** BambooHR employee id — see the QuickBooks client for why it's carried. */
  externalId: string | null;
  firstName: string;
  lastName: string;
  sex: Sex | null;
  zip: string | null;
  dateOfBirth: string | null;
  dependentMethod: "manual" | "none";
  enrolling: boolean;
  dependents: never[];
}

export interface CensusBambooHrBusinessWire {
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
}

export interface CensusBambooHrResult {
  connected: boolean;
  companyDomain: string | null;
  companyName: string | null;
  business: CensusBambooHrBusinessWire | null;
  members: CensusBambooHrMemberWire[];
  employeeCount: number;
  hasCompanyInfo: boolean;
  /**
   * Plain-language notes about fields BambooHR withheld. BambooHR's report
   * endpoint omits keys the token cannot see instead of erroring, so without
   * these a roster with blank DOBs looks like our bug rather than a permission
   * boundary in the employer's BambooHR account.
   */
  fieldWarnings: string[];
  /**
   * Former employees left out of the roster. The pull deliberately asks
   * BambooHR for terminated rows too, so this is a real count, not a guess.
   */
  excludedFormerCount: number;
}

/** Start the BambooHR OAuth for the caller's census draft. */
export function authorizeCensusBambooHr(
  companyDomain: string,
): Promise<CensusBambooHrAuthorizeResponse> {
  return client().post<CensusBambooHrAuthorizeResponse>(`${BAMBOO_PATH}/authorize`, {
    companyDomain,
  });
}

/** Read the persisted BambooHR pull for the caller's census draft. */
export function getCensusBambooHrResult(): Promise<CensusBambooHrResult> {
  return client().get<CensusBambooHrResult>(`${BAMBOO_PATH}/result`);
}
